/**
 * Helpers de chamada de API para a area /configuracoes/acessos.
 *
 * Centraliza endpoints novos do RBAC recurso:acao para que cada sub-tela
 * apenas importe a funcao desejada. `request` vem do AuthContext e
 * cuida do refresh automatico de JWT.
 */

const PREFIXO_USUARIO = "/api/usuarios";
const PREFIXO_CARGO = "/api/cargos/rbac";

export function fetchHubAcessos(request) {
  return request("/api/acessos/hub");
}

export function fetchCatalogoPermissoes(request, agrupado = true) {
  const qs = agrupado ? "?agrupado=true" : "";
  return request(`/api/permissoes${qs}`);
}

// ----------- Usuarios -----------
export function fetchUsuariosLista(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.search) usp.set("search", params.search);
  if (params.cargo) usp.set("cargo", params.cargo);
  if (params.ativo !== undefined && params.ativo !== "") usp.set("ativo", params.ativo);
  if (params.pagina) usp.set("pagina", params.pagina);
  if (params.por_pagina) usp.set("por_pagina", params.por_pagina);
  const qs = usp.toString();
  return request(`${PREFIXO_USUARIO}${qs ? `?${qs}` : ""}`);
}

/** O maximo que o backend aceita por pagina (`core.pagination.POR_PAGINA_MAXIMO`). */
export const USUARIOS_POR_PAGINA = 200;
/** Trava de seguranca do laco de paginas: 50 x 200 = 10 mil usuarios. */
const MAXIMO_DE_PAGINAS = 50;

/**
 * A lista INTEIRA de usuarios que passa nos filtros, juntando as paginas.
 *
 * O backend pagina (20 por padrao). A tela nao tem paginador e ainda filtra
 * MFA e 1o acesso no navegador, entao pedir so a primeira pagina escondia
 * quem ficava da 21a posicao em diante — a pessoa so aparecia buscando pelo
 * nome. Devolve `{ data, total }`.
 */
export async function fetchTodosUsuarios(request, params = {}) {
  const primeira = await fetchUsuariosLista(request, { ...params, pagina: 1, por_pagina: USUARIOS_POR_PAGINA });
  const total = primeira?.total || 0;
  const data = [...(primeira?.data || [])];
  for (let pagina = 2; data.length < total && pagina <= MAXIMO_DE_PAGINAS; pagina += 1) {
    // Em sequencia de proposito: sao raras e a ordem das paginas importa.
    // eslint-disable-next-line no-await-in-loop
    const proxima = await fetchUsuariosLista(request, { ...params, pagina, por_pagina: USUARIOS_POR_PAGINA });
    const lote = proxima?.data || [];
    if (!lote.length) break;
    data.push(...lote);
  }
  return { data, total };
}

export function fetchUsuario(request, id) {
  return request(`${PREFIXO_USUARIO}/${id}`);
}

export function fetchUsuarioEfetivas(request, id) {
  return request(`${PREFIXO_USUARIO}/${id}/efetivas`);
}

export function putUsuarioOverrides(request, id, overrides, opts = {}) {
  return request(`${PREFIXO_USUARIO}/${id}/overrides`, {
    method: "PUT",
    body: JSON.stringify({
      overrides,
      aceite_conflito: opts.aceiteConflito || false,
      justificativa: opts.justificativa || "",
    }),
    // Repassa silentError ao wrapper request (auto-toast global) quando a
    // tela trata o erro manualmente (ex.: SoD pendente vira fluxo proprio).
    silentError: Boolean(opts.silentError),
  });
}

export function patchUsuario(request, id, dados) {
  return request(`${PREFIXO_USUARIO}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function patchUsuarioAtivar(request, id, ativo) {
  return request(`${PREFIXO_USUARIO}/${id}/ativar`, {
    method: "PATCH",
    body: JSON.stringify({ ativo }),
  });
}

export function resetarSenha(request, id) {
  return request(`${PREFIXO_USUARIO}/${id}/resetar-senha`, { method: "POST" });
}

// ----------- Cargos -----------
export function fetchCargos(request, comTotais = true) {
  const qs = comTotais ? "?com_totais=true" : "";
  return request(`${PREFIXO_CARGO}${qs}`);
}

export function fetchCargosTree(request) {
  return request(`${PREFIXO_CARGO}/tree`);
}

export function fetchCargo(request, id) {
  return request(`${PREFIXO_CARGO}/${id}`);
}

export function patchCargo(request, id, dados) {
  return request(`${PREFIXO_CARGO}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

// CRUD de cargos (Sprint 7).
export function criarCargo(request, dados) {
  return request(PREFIXO_CARGO, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function deletarCargo(request, id) {
  return request(`${PREFIXO_CARGO}/${id}`, { method: "DELETE" });
}

export function fetchCargoEfetivas(request, id) {
  return request(`${PREFIXO_CARGO}/${id}/efetivas`);
}

export function fetchCargoPermissoes(request, id) {
  return request(`${PREFIXO_CARGO}/${id}/permissoes`);
}

export function putCargoPermissoes(request, id, codigos, opts = {}) {
  return request(`${PREFIXO_CARGO}/${id}/permissoes`, {
    method: "PUT",
    body: JSON.stringify({
      codigos,
      aceite_conflito: opts.aceiteConflito || false,
      justificativa: opts.justificativa || "",
    }),
  });
}

export function fetchCargoUsuarios(request, id) {
  return request(`${PREFIXO_CARGO}/${id}/usuarios`);
}

// ----------- Auditoria -----------
export function fetchAuditoria(request, filtros = {}) {
  const usp = new URLSearchParams();
  Object.entries(filtros).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") usp.set(k, v);
  });
  const qs = usp.toString();
  return request(`/api/auditoria${qs ? `?${qs}` : ""}`);
}

export function fetchAuditoriaEvento(request, id) {
  return request(`/api/auditoria/${id}`);
}

// ----------- SoD -----------
export function fetchConflitosSod(request, apenasAtivos = false) {
  const qs = apenasAtivos ? "?ativo=true" : "";
  return request(`/api/conflitos-sod${qs}`);
}

export function fetchConflitosSodViolacoes(request) {
  return request("/api/conflitos-sod/violacoes");
}

export function criarConflitoSod(request, dados) {
  return request("/api/conflitos-sod", {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function patchConflitoSod(request, id, dados) {
  return request(`/api/conflitos-sod/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function deletarConflitoSod(request, id) {
  return request(`/api/conflitos-sod/${id}`, { method: "DELETE" });
}

// ----------- Convite por email (Sprint 6) -----------
export function enviarConvite(request, usuarioId) {
  return request(`${PREFIXO_USUARIO}/${usuarioId}/enviar-convite`, { method: "POST" });
}

// ----------- MFA (Sprint 5) -----------
export function fetchMfaStatus(request) {
  return request("/api/me/mfa");
}

// Sprint 9 — diagnostico: front consulta antes de iniciar enrollment.
// Devolve `{disponivel: bool, motivo: string|null}`. Se indisponivel,
// a UI mostra mensagem clara em vez de tropecar num 503 mais adiante.
export function fetchMfaSaude(request) {
  return request("/api/auth/mfa/saude");
}

export function postMfaSetupSecret(request) {
  return request("/api/auth/mfa/setup-secret", { method: "POST" });
}

export function postMfaVerifyAndEnable(request, codigo) {
  return request("/api/auth/mfa/verify-and-enable", {
    method: "POST",
    body: JSON.stringify({ codigo }),
  });
}

export function postMfaDisable(request, senha, codigo) {
  return request("/api/auth/mfa/disable", {
    method: "POST",
    body: JSON.stringify({ senha, codigo }),
  });
}

export function postMfaRegenerarCodigos(request, codigo) {
  return request("/api/auth/mfa/regenerar-codigos", {
    method: "POST",
    body: JSON.stringify({ codigo }),
  });
}

export function patchUsuarioMfaForcarReset(request, usuarioId) {
  return request(`${PREFIXO_USUARIO}/${usuarioId}/mfa/forcar-reset`, { method: "PATCH" });
}

// ----------- Criação de usuario -----------
export function criarUsuario(request, dados) {
  return request(PREFIXO_USUARIO, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}
